import { PermissionContext } from '@ardoq/access-control';
import {
  APIComponentAttributes,
  APIEntityType,
  APIReferenceAttributes,
  APIReferenceType,
  ArdoqId,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { DEFAULT_ARDOQ_I18NLOCALE, t, type I18nLocale } from '@ardoq/i18n';
import { invariant } from '@ardoq/invariant';
import {
  FieldType,
  getArrowTypeLabel,
  getReferenceTypeByReferenceId,
} from '@ardoq/renderers';
import {
  enhancedScopeDataOperations,
  scopeDataOperations,
} from '@ardoq/scope-data';
import { SelectOptionsOrGroups } from '@ardoq/select';
import { getFieldOptions } from 'appModelStateEdit/propertiesEditor/fieldUtils';
import { CARDINALITY_ATTRIBUTES } from 'appModelStateEdit/propertiesEditor/referencePropertiesEditor/consts';
import { getCardinalityArrowTypes } from 'modelInterface/util';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { isMissingDisplayText } from 'scopeData/editors/validators';
import { getFeatureContext } from 'streams/featureContext$';
import { IMPLICIT_REFTYPE_ID } from '../../../models/model';
import {
  IntendedRefTypes,
  isIntendedRefType,
  processReferenceTypes,
} from '../../../utils/metamodelConstraints';
import type { EditorProperty } from '../types';
import {
  CustomPropertyOptionGetterMap,
  TypeOptionsExtraContext,
  DefaultPropertyOptionGetterMap,
} from '../optionGetterTypes';

type ListOption = { value: string | number; label: string };

export type TripleWarningData = {
  sourceType: string;
  referenceType: string;
  targetType: string;
  validReferenceTypeCount: number;
};

const referenceTypeToOption = (
  referenceType: APIReferenceType
): ListOption => ({
  value: referenceType.id,
  label: referenceType.name,
});

const getTypeOptions = (
  referenceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  _permissionContext: PermissionContext,
  extraContext?: TypeOptionsExtraContext
): SelectOptionsOrGroups<string | number> => {
  const reference = enhancedScopeDataOperations.getReference(
    enhancedScopeData,
    referenceId
  );
  const modelId = reference?.model;
  if (!reference || !modelId) {
    return [];
  }

  let availableRefTypes = extraContext?.refTypesSortedByMostUsed ?? [];
  if (!availableRefTypes.length) {
    availableRefTypes = enhancedScopeDataOperations.getModelReferenceTypes(
      enhancedScopeData,
      modelId
    );
  }

  const sourceType =
    enhancedScopeDataOperations.getComponent(
      enhancedScopeData,
      reference.source
    )?.type ?? '';
  const targetType =
    enhancedScopeDataOperations.getComponent(
      enhancedScopeData,
      reference.target
    )?.type ?? '';

  const processed = processReferenceTypes(
    availableRefTypes,
    extraContext?.intendedRefTypes ?? null,
    extraContext?.isEnforcementEnabled ?? false,
    sourceType,
    targetType
  );

  if (!processed.hasGrouping) {
    return processed.availableRefTypes.map(referenceTypeToOption);
  }

  const locale = extraContext?.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE;
  return [
    {
      label: t(
        locale,
        'propertiesEditor:referenceTypes.supported',
        'Supported'
      ),
      options: processed.supportedRefTypes.map(referenceTypeToOption),
    },
    {
      label: t(
        locale,
        'propertiesEditor:referenceTypes.other',
        'Other reference types'
      ),
      options: processed.otherRefTypes.map(referenceTypeToOption),
    },
  ];
};

export const getInitialReferenceTypeId = (
  availableRefTypes: APIReferenceType[],
  intendedRefTypes: IntendedRefTypes | null,
  isEnforcementEnabled: boolean,
  sourceType: string | null,
  targetType: string | null
): number => {
  const processed = processReferenceTypes(
    availableRefTypes,
    intendedRefTypes,
    isEnforcementEnabled,
    sourceType,
    targetType
  );

  if (!processed.hasGrouping) {
    return processed.availableRefTypes[0]?.id ?? IMPLICIT_REFTYPE_ID;
  }

  return (
    processed.supportedRefTypes[0]?.id ??
    processed.otherRefTypes[0]?.id ??
    IMPLICIT_REFTYPE_ID
  );
};

const toComponentPathOption = (
  component: APIComponentAttributes,
  enhancedScopeData: EnhancedScopeData
): { value: ArdoqId; label: string } => {
  const path = [
    ...scopeDataOperations.getAncestorBreadcrumbs(
      component,
      enhancedScopeData.componentsById
    ),
    component.name,
  ].join(' / ');
  return {
    value: component._id,
    label: `${path} [${component.type}]`,
  };
};

const getSourceOptions = (
  referenceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  permissionContext: PermissionContext
): SelectOptionsOrGroups<string> => {
  const reference = enhancedScopeData.referencesById[referenceId];
  const rootWorkspace = reference.rootWorkspace;

  return enhancedScopeData.components
    .filter(component => {
      if (component.rootWorkspace !== rootWorkspace) {
        return false;
      }
      return componentAccessControlOperation.canEditComponent({
        component,
        permissionContext,
        featureContext: getFeatureContext(),
      });
    })
    .map(component => toComponentPathOption(component, enhancedScopeData));
};

const getTargetOptions = (
  _entityId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  _permissionContext: PermissionContext
): ListOption[] =>
  enhancedScopeData.components.map(component =>
    toComponentPathOption(component, enhancedScopeData)
  );

const getCardinalityOptions =
  (suffix = '') =>
  (
    _entityId: ArdoqId,
    _enhancedScopeData: EnhancedScopeData,
    _permissionContext: PermissionContext
  ): ListOption[] => {
    return getCardinalityArrowTypes().map(arrowType => ({
      value: `${arrowType}${suffix}`,
      label: getArrowTypeLabel(arrowType),
    }));
  };

export const defaultPropertyOptionGetterMap: DefaultPropertyOptionGetterMap =
  new Map([
    [FieldType.TYPE_ID_REFERENCE, getTypeOptions],
    [FieldType.SOURCE, getSourceOptions],
    [FieldType.TARGET, getTargetOptions],
    [FieldType.SOURCE_CARDINALITY, getCardinalityOptions('Start')],
    [FieldType.TARGET_CARDINALITY, getCardinalityOptions()],
  ]);

export const customPropertyOptionGetterMap: CustomPropertyOptionGetterMap =
  new Map([
    [FieldType.LIST, getFieldOptions],
    [FieldType.SELECT_MULTIPLE_LIST, getFieldOptions],
  ]);

export const isCardinalityAttributeAllowed =
  (referenceId: ArdoqId, enhancedScopeData: EnhancedScopeData) =>
  (attributeName: string) => {
    if (!CARDINALITY_ATTRIBUTES.includes(attributeName)) {
      return true;
    }
    const type = getReferenceTypeByReferenceId(referenceId, enhancedScopeData);
    return Boolean(type?.hasCardinality);
  };

export const getTripleWarningProperty = (
  referenceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  intendedRefTypes?: IntendedRefTypes
): EditorProperty | null => {
  if (!isViolatingTriples(referenceId, enhancedScopeData, intendedRefTypes)) {
    return null;
  }

  return {
    type: FieldType.TRIPLES_WARNING,
    name: '',
    label: '',
    value: getTripleWarningData(
      referenceId,
      enhancedScopeData,
      intendedRefTypes
    ),
    isDirty: false,
  };
};

const isViolatingTriples = (
  referenceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  intendedRefTypes?: IntendedRefTypes
): boolean => {
  if (!intendedRefTypes) {
    // Triple enforcement is not enabled, so no violation
    return false;
  }

  const reference = enhancedScopeDataOperations.getReference(
    enhancedScopeData,
    referenceId
  );
  invariant(reference, 'Reference being edited should be in scope data');

  const referenceType =
    enhancedScopeDataOperations.getReferenceTypeByReferenceId(
      enhancedScopeData,
      referenceId
    );
  invariant(referenceType, 'Reference type should be in scope data');

  const sourceComponentType =
    enhancedScopeDataOperations.getComponentTypeByComponentId(
      enhancedScopeData,
      reference.source
    );
  invariant(
    sourceComponentType,
    'Source component type should be in scope data'
  );

  const targetComponentType =
    enhancedScopeDataOperations.getComponentTypeByComponentId(
      enhancedScopeData,
      reference.target
    );
  invariant(
    targetComponentType,
    'Target component type should be in scope data'
  );

  return isIntendedRefType(
    intendedRefTypes,
    sourceComponentType.name,
    targetComponentType.name,
    referenceType.name
  );
};

export const toSourceTargetIdTuple = (
  reference: APIReferenceAttributes
): [ArdoqId, ArdoqId] => [reference.source, reference.target];

export const sourceTargetIdTupleToNameTuple =
  (componentsById: EnhancedScopeData['componentsById']) =>
  ([sourceId, targetId]: [ArdoqId, ArdoqId]): [string, string] => [
    componentsById[sourceId]?.name ?? '',
    componentsById[targetId]?.name ?? '',
  ];

const getTripleWarningData = (
  referenceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  intendedRefTypes?: IntendedRefTypes
): TripleWarningData | null => {
  const reference = enhancedScopeDataOperations.getReference(
    enhancedScopeData,
    referenceId
  );

  if (!reference) {
    return null;
  }

  const sourceComponent = enhancedScopeDataOperations.getComponent(
    enhancedScopeData,
    reference.source
  );
  const targetComponent = enhancedScopeDataOperations.getComponent(
    enhancedScopeData,
    reference.target
  );
  const referenceType =
    enhancedScopeDataOperations.getReferenceTypeByReferenceId(
      enhancedScopeData,
      referenceId
    );

  if (!sourceComponent || !targetComponent || !referenceType) {
    return null;
  }

  const sourceComponentType =
    enhancedScopeDataOperations.getComponentTypeByComponentId(
      enhancedScopeData,
      sourceComponent._id
    );
  const targetComponentType =
    enhancedScopeDataOperations.getComponentTypeByComponentId(
      enhancedScopeData,
      targetComponent._id
    );

  // Count the approved *named* reference types for this source → target pair
  // (excluding the Implicit type itself, since the nudge points the user
  // towards the named alternatives).
  const approvedReferenceTypeNames = (
    intendedRefTypes?.[sourceComponentType.name]?.[targetComponentType.name] ??
    []
  ).filter(name => name !== 'Implicit');
  const validReferenceTypeCount = approvedReferenceTypeNames.length;

  return {
    sourceType: sourceComponentType?.name || 'Unknown',
    referenceType: referenceType.name,
    targetType: targetComponentType?.name || 'Unknown',
    validReferenceTypeCount,
  };
};

export const getReferenceErrorMessage = (
  entityType: APIEntityType,
  entityIDs: ArdoqId[],
  intendedRefTypes: IntendedRefTypes | undefined,
  enhancedScopeData: EnhancedScopeData,
  i18nLocale: I18nLocale
): string | null => {
  if (entityType !== APIEntityType.REFERENCE) return null;
  if (!intendedRefTypes) return null; // Feature flags are not active when this is missing

  const hasError = entityIDs.some(entityId =>
    isMissingDisplayText(entityId, enhancedScopeData)
  );

  if (hasError) {
    return t(
      i18nLocale,
      'propertiesEditor:errors.displayTextRequired',
      'Display text is required.'
    );
  }

  return null;
};
